import { e } from './modules/utils.js';
import { PRESET_COLORS, DEFAULT_SUB_COLUMNS } from './modules/state.js';
import { loadOverrides, saveOverrides, getOverridesKey, cloudSave } from './modules/storage.js';
import { saveCardOrder } from './modules/drag-drop.js';
import { boardData, navStack, expandedCards } from './core.js';
import { findCardById, mergeOverrides, getCardSchedule } from './data.js';
// NOTE: renderFromState is imported from render.js (circular dep — safe because
// these are functions called at runtime, not at module evaluation time)
import { renderFromState } from './render.js';

// ── Saved flash indicator ──
let _flashTimer = null;
export function flashSaved() {
  const el = document.getElementById('header-username');
  if (!el) return;
  el.classList.add('saved-flash');
  clearTimeout(_flashTimer);
  _flashTimer = setTimeout(() => el.classList.remove('saved-flash'), 1200);
}

export function flashToast(msg) {
  let toast = document.getElementById('conflict-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'conflict-toast';
    toast.style.cssText = 'position:fixed;top:12px;left:50%;transform:translateX(-50%);background:#e0af68;color:#1a1b26;padding:10px 20px;border-radius:8px;font-size:0.85rem;font-weight:600;z-index:10001;transition:opacity 0.3s';
    document.body.appendChild(toast);
  }
  toast.textContent = msg;
  toast.style.opacity = '1';
  setTimeout(() => { toast.style.opacity = '0'; }, 3000);
}

export function renameColumn(colId, newName) {
  const ov = loadOverrides();
  if (!ov.columnRenames) ov.columnRenames = {};
  ov.columnRenames[colId] = newName.trim() || 'Untitled';
  // Immediate local + cloud save — bypass debounce for renames
  localStorage.setItem(getOverridesKey(), JSON.stringify(ov));
  cloudSave(ov);
  renderFromState();
}

export function archiveColumn(colId) {
  const ov = loadOverrides();
  if (!ov.archivedColumns) ov.archivedColumns = [];
  if (!ov.archivedColumns.includes(colId)) ov.archivedColumns.push(colId);
  saveOverrides(ov);
  renderFromState();
}

export function deleteColumn(colId) {
  // For user-added columns: remove from addedColumns entirely
  // For base columns: treat as archive (can't delete template data)
  const ov = loadOverrides();
  if (ov.addedColumns) {
    const idx = ov.addedColumns.findIndex(c => c.id === colId);
    if (idx !== -1) {
      ov.addedColumns.splice(idx, 1);
      // Also clean up its rename if any
      if (ov.columnRenames) delete ov.columnRenames[colId];
      if (ov.rootColumnOrder) ov.rootColumnOrder = ov.rootColumnOrder.filter(id => id !== colId);
      saveOverrides(ov);
      renderFromState();
      return;
    }
  }
  // Base column — archive it
  archiveColumn(colId);
}

export function addColumn(name) {
  const colId = 'col-' + Date.now();
  const ov = loadOverrides();
  if (!ov.addedColumns) ov.addedColumns = [];
  ov.addedColumns.push({ id: colId, name: name.trim() || 'New Column' });
  // Append to root column order
  if (!ov.rootColumnOrder) {
    ov.rootColumnOrder = (boardData?.columns || [])
      .filter(c => !ov.archivedColumns?.includes(c.id))
      .map(c => c.id);
  }
  ov.rootColumnOrder.push(colId);
  saveOverrides(ov);
  renderFromState();
  return colId;
}

// ── Breadcrumb ──
export function isParentCard(proj) {
  return Array.isArray(proj.children);
}

// ── Archive ──
export function toggleArchive(cardId) {
  const snapshotOv = JSON.parse(JSON.stringify(loadOverrides()));
  const ov = loadOverrides();
  if (!ov.archivedCards) ov.archivedCards = {};
  const isArchiving = !ov.archivedCards[cardId];
  if (isArchiving) {
    ov.archivedCards[cardId] = true;
  } else {
    delete ov.archivedCards[cardId];
  }
  saveOverrides(ov);
  renderFromState();
  if (isArchiving) {
    const card = findCardById(cardId, mergeOverrides(boardData));
    const label = card ? `"${card.name}" archived` : 'Card archived';
    showUndoToast(label, snapshotOv);
  }
}

// ── Flag helpers ──
export function isFlagged(cardId) {
  const ov = loadOverrides();
  return !!(ov.flaggedCards && ov.flaggedCards[cardId]);
}

export function countFlaggedDescendants(card, ov) {
  if (!ov.flaggedCards) return 0;
  let count = 0;
  if (!card.children) return 0;
  for (const child of card.children) {
    if (ov.flaggedCards[child.id]) count++;
    count += countFlaggedDescendants(child, ov);
  }
  return count;
}

// ── Card rename ──
export function attachCardRename(card, proj) {
  const nameEl = card.querySelector('.card-name');
  if (!nameEl) return;
  nameEl.title = 'Double-click to rename';
  nameEl.addEventListener('dblclick', (e) => {
    e.stopPropagation();
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'card-name-edit';
    input.value = proj.name;
    nameEl.replaceWith(input);
    input.focus();
    input.select();

    function saveName() {
      const newName = input.value.trim();
      if (newName && newName !== proj.name) {
        const ov2 = loadOverrides();
        if (!ov2.cardNames) ov2.cardNames = {};
        ov2.cardNames[proj.id] = newName;
        const navEntry = navStack.find(n => n.id === proj.id);
        if (navEntry) navEntry.name = newName;
        saveOverrides(ov2);
      }
      renderFromState();
    }

    input.addEventListener('blur', saveName);
    input.addEventListener('keydown', (ke) => {
      ke.stopPropagation();
      if (ke.key === 'Enter') { ke.preventDefault(); input.removeEventListener('blur', saveName); saveName(); }
      if (ke.key === 'Escape') { input.removeEventListener('blur', saveName); renderFromState(); }
    });
    input.addEventListener('click', (ce) => ce.stopPropagation());
  });
}

// ── Column rename inline edit ──
export function startColRename(colEl, colId, currentName) {
  const nameEl = colEl.querySelector('.col-name');
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'col-rename-input';
  input.value = currentName;
  nameEl.replaceWith(input);
  input.focus();
  input.select();
  function commit() {
    const newName = input.value.trim();
    if (newName && newName !== currentName) renameColumn(colId, newName);
    else { renderFromState(); }
  }
  input.addEventListener('blur', commit);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') { renderFromState(); }
  });
}

// ── Undo toast ──
let _undoTimer = null;
let _undoSnapshot = null;

export function showUndoToast(label, snapshotOv) {
  clearTimeout(_undoTimer);
  _undoSnapshot = { overrides: JSON.parse(JSON.stringify(snapshotOv)), label };

  let toast = document.getElementById('undo-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'undo-toast';
    toast.className = 'undo-toast';
    document.body.appendChild(toast);
  }
  toast.innerHTML = `<span class="undo-label">${e(label)}</span><button class="undo-btn">Undo</button>`;
  toast.classList.add('visible');

  toast.querySelector('.undo-btn').addEventListener('click', () => {
    if (_undoSnapshot) {
      saveOverrides(_undoSnapshot.overrides);
      renderFromState();
      _undoSnapshot = null;
    }
    dismissUndoToast();
  });

  _undoTimer = setTimeout(dismissUndoToast, 8000);
}

export function dismissUndoToast() {
  clearTimeout(_undoTimer);
  _undoSnapshot = null;
  const toast = document.getElementById('undo-toast');
  if (toast) toast.classList.remove('visible');
}

// ── Duplicate card ──
export function duplicateCard(proj, columnId) {
  const newId = 'dup-' + Date.now();
  const today = new Date().toISOString().split('T')[0];

  // Deep-clone the card and assign new IDs to everything
  const newCard = {
    id: newId,
    name: proj.name + ' (copy)',
    color: proj.color,
    createdAt: today,
    dueDate: proj.dueDate || null,
    status: proj.status || '',
    outcome: proj.outcome || '',
    contacts: JSON.parse(JSON.stringify(proj.contacts || [])),
    comms: [],  // Don't copy comms history — fresh start
    columnId,
  };

  if (proj.children) {
    // Parent card: duplicate children with new IDs
    newCard.children = (proj.children || []).map((child, i) => ({
      id: newId + '-c' + i,
      name: child.name,
      color: child.color,
      createdAt: today,
      dueDate: child.dueDate || null,
      status: child.status || '',
      outcome: child.outcome || '',
      tasks: (child.tasks || []).map((t, j) => ({
        id: newId + '-c' + i + '-t' + j,
        text: t.text,
        done: false,  // Reset completion state
        dueDate: t.dueDate || null,
        completedAt: null,
      })),
      contacts: JSON.parse(JSON.stringify(child.contacts || [])),
      comms: [],
      columnId: child.columnId || null,
    }));
    newCard.subBoardColumns = JSON.parse(JSON.stringify(proj.subBoardColumns || []));
  } else {
    // Leaf card: duplicate tasks, reset completion
    newCard.tasks = (proj.tasks || []).map((t, i) => ({
      id: newId + '-t' + i,
      text: t.text,
      done: false,
      dueDate: t.dueDate || null,
      completedAt: null,
    }));
  }

  const ov = loadOverrides();
  if (!ov.addedProjects) ov.addedProjects = [];
  // Insert right after the original card
  const origIdx = ov.addedProjects.findIndex(p => p.id === proj.id);
  if (origIdx !== -1) {
    ov.addedProjects.splice(origIdx + 1, 0, newCard);
  } else {
    ov.addedProjects.unshift(newCard);
  }
  saveOverrides(ov);

  // Expand the new card so the user sees it's ready to edit
  expandedCards.add(newId);
  renderFromState();

  // Scroll to & flash the new card
  setTimeout(() => {
    const newCardEl = document.querySelector(`[data-project-id="${newId}"]`);
    if (newCardEl) {
      newCardEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      newCardEl.classList.add('card-flash');
      setTimeout(() => newCardEl.classList.remove('card-flash'), 1200);
    }
  }, 100);
}

export function deleteProject(projectId) {
  const snapshotOv = JSON.parse(JSON.stringify(loadOverrides()));
  const card = findCardById(projectId, mergeOverrides(boardData));
  const label = card ? `"${card.name}" deleted` : 'Card deleted';

  const ov = loadOverrides();
  if (!ov.deletedProjects) ov.deletedProjects = [];
  if (!ov.deletedProjects.includes(projectId)) ov.deletedProjects.push(projectId);
  if (ov.columnOrder) {
    for (const colId in ov.columnOrder) {
      ov.columnOrder[colId] = ov.columnOrder[colId].filter(id => id !== projectId);
    }
  }
  if (ov.addedProjects) ov.addedProjects = ov.addedProjects.filter(p => p.id !== projectId);
  if (ov.addedChildren) {
    delete ov.addedChildren[projectId];
    for (const parentId in ov.addedChildren) {
      ov.addedChildren[parentId] = ov.addedChildren[parentId].filter(c => c.id !== projectId);
    }
  }
  saveOverrides(ov);
  renderFromState();
  showUndoToast(label, snapshotOv);
}

export function addNewProject(columnId, name, color, hasSubItems) {
  const projId = 'custom-' + Date.now();
  addNewProjectWithId(columnId, projId, name, color, hasSubItems);
  renderFromState();
  saveCardOrder();
}

export function addNewProjectWithId(columnId, projId, name, color, hasSubItems) {
  const createdAt = new Date().toISOString().split('T')[0];
  const ov = loadOverrides();

  if (navStack.length === 0) {
    if (!ov.addedProjects) ov.addedProjects = [];
    const newProj = { id: projId, name, color, createdAt, columnId };
    if (hasSubItems) {
      newProj.children = [];
      newProj.subBoardColumns = JSON.parse(JSON.stringify(DEFAULT_SUB_COLUMNS));
    } else {
      newProj.tasks = [];
      newProj.status = '';
      newProj.contacts = [];
      newProj.comms = [];
    }
    ov.addedProjects.push(newProj);
  } else {
    const parentId = navStack[navStack.length - 1].id;
    if (!ov.addedChildren) ov.addedChildren = {};
    if (!ov.addedChildren[parentId]) ov.addedChildren[parentId] = [];
    const newChild = { id: projId, name, color, createdAt, columnId };
    if (hasSubItems) {
      newChild.children = [];
      newChild.subBoardColumns = JSON.parse(JSON.stringify(DEFAULT_SUB_COLUMNS));
    } else {
      newChild.tasks = [];
      newChild.status = '';
      newChild.contacts = [];
      newChild.comms = [];
    }
    ov.addedChildren[parentId].push(newChild);
  }

  saveOverrides(ov);
}

// ── Save functions ──
export function saveStatus(cardId, status) {
  const ov = loadOverrides();
  if (!ov.cardStatus) ov.cardStatus = {};
  ov.cardStatus[cardId] = status;
  saveOverrides(ov);
}

export function saveCardDueDate(cardId, dateStr) {
  const ov = loadOverrides();
  if (!ov.cardDueDates) ov.cardDueDates = {};
  if (dateStr) ov.cardDueDates[cardId] = dateStr;
  else delete ov.cardDueDates[cardId];
  saveOverrides(ov);
  renderFromState();
}

export function saveCardEndDate(cardId, dateStr) {
  const ov = loadOverrides();
  if (!ov.cardEndDates) ov.cardEndDates = {};
  if (dateStr) ov.cardEndDates[cardId] = dateStr;
  else delete ov.cardEndDates[cardId];
  saveOverrides(ov);
}

export function openDateRangePopover(projId, proj, anchorEl) {
  document.querySelectorAll(".drp-inline").forEach(el => el.remove());

  const ov = loadOverrides();
  // Only show pinned start if user explicitly set it — don't pre-fill with auto-derived date
  const sched = getCardSchedule(proj);
  const hasExplicitStart = ov.cardDueDates && ov.cardDueDates[projId];
  const currentStart = hasExplicitStart ? ov.cardDueDates[projId] : "";
  const autoStart    = (!hasExplicitStart && sched && sched.derived) ? sched.start : "";
  const currentEnd   = (ov.cardEndDates  && ov.cardEndDates[projId])  || proj.endDate  || "";

  const mn = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  function fmtShort(d) {
    if (!d) return "";
    const dt = new Date(d + "T00:00:00");
    return `${mn[dt.getMonth()]} ${dt.getDate()}`;
  }

  // Parse multiple formats
  const M = {jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12};
  function parseDate(val) {
    if (!val || val.toLowerCase().trim() === "clear") return null;
    const s = val.trim();

    const numeric = s.match(/^(\d{1,2})[\/\-](\d{1,2})(?:[\/\-](\d{2,4}))?$/);
    if (numeric) {
      const mo = parseInt(numeric[1]);
      const day = parseInt(numeric[2]);
      const cy = new Date().getFullYear();
      let y = numeric[3] ? parseInt(numeric[3]) : cy;
      if (numeric[3] && numeric[3].length === 2) y += 2000;
      if (!numeric[3]) { const c = new Date(cy,mo-1,day); if (c < new Date()) y = cy+1; }
      if (mo < 1 || mo > 12 || day < 1 || day > 31) return undefined;
      return `${y}-${String(mo).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    }

    const named = s.match(/^([a-zA-Z]{3,})\s+(\d{1,2})(?:\s+(\d{4}))?$/);
    if (named) {
      const mo = M[named[1].toLowerCase().slice(0,3)];
      if (!mo) return undefined;
      const day = parseInt(named[2]);
      const cy = new Date().getFullYear();
      let y = named[3] ? parseInt(named[3]) : cy;
      if (!named[3]) { const c = new Date(cy,mo-1,day); if (c < new Date()) y = cy+1; }
      return `${y}-${String(mo).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    }

    return undefined;
  }

  // Build inline row
  const wrap = document.createElement("span");
  wrap.className = "drp-inline";

  const startInput = document.createElement("input");
  startInput.type = "text";
  startInput.className = "card-date-input drp-s";
  startInput.placeholder = autoStart ? `Auto: ${fmtShort(autoStart)}` : "Start";
  startInput.value = fmtShort(currentStart);

  const sep = document.createElement("span");
  sep.className = "drp-sep";
  sep.textContent = "\u2192";

  const endInput = document.createElement("input");
  endInput.type = "text";
  endInput.className = "card-date-input drp-e";
  endInput.placeholder = "End";
  endInput.value = fmtShort(currentEnd);

  wrap.appendChild(startInput);
  wrap.appendChild(sep);
  wrap.appendChild(endInput);

  anchorEl.replaceWith(wrap);
  startInput.focus();
  startInput.select();

  const _renderFromState = () => { renderFromState(); };

  function save() {
    const sv = startInput.value.trim();
    const ev = endInput.value.trim();
    const start = sv ? parseDate(sv) : null;
    const end   = ev ? parseDate(ev) : null;
    if (start === undefined || end === undefined) { _renderFromState(); return; }
    saveCardDueDate(projId, start);
    saveCardEndDate(projId, end);
    _renderFromState();
  }

  startInput.addEventListener("keydown", ke => {
    ke.stopPropagation();
    if (ke.key === "Tab") { ke.preventDefault(); endInput.focus(); endInput.select(); }
    if (ke.key === "Enter") { ke.preventDefault(); save(); }
    if (ke.key === "Escape") _renderFromState();
  });

  endInput.addEventListener("keydown", ke => {
    ke.stopPropagation();
    if (ke.key === "Enter") { ke.preventDefault(); save(); }
    if (ke.key === "Escape") _renderFromState();
  });

  endInput.addEventListener("blur", () => setTimeout(save, 150));
  startInput.addEventListener("blur", () => {
    setTimeout(() => {
      if (document.activeElement !== endInput) save();
    }, 150);
  });

  [startInput, endInput].forEach(i => i.addEventListener("click", e => e.stopPropagation()));
}
